<template>
    <layout>
        <a-breadcrumb>
            <a-breadcrumb-item data-cy="breadcrumb-main">
                {{ $t('globals.navigation.header.user_settings.title') }}
            </a-breadcrumb-item>
            <a-breadcrumb-item data-cy="breadcrumb-sub">
                {{ tabTitle(this.default_tab) }}
            </a-breadcrumb-item>
        </a-breadcrumb>

        <div class="shadow-xl settings">
            <a-tabs
                default-active-key="general"
                :active-key="default_tab"
                tab-position="left"
                size="default"
                @change="onTabChange"
                data-cy="tab-container"
            >
                <a-tab-pane key="main" :tab="tabTitle('main')" disabled />
                <a-tab-pane key="general" :tab="tabTitle('general')">
                    <tab-general
                        :user="user"
                        :processing="processing"
                        :validation="validation"
                        @formSubmit="handleSubmit"
                        ref="GeneralTab"
                        @is-form-dirty="isFormDirty"
                    />
                </a-tab-pane>
                <a-tab-pane key="security" :tab="tabTitle('security')">
                    <tab-security
                        :password_change="password_change"
                        :processing="processing"
                        :validation="validation"
                        @formSubmit="handlePasswordSubmit"
                        ref="SecurityTab"
                        @is-form-dirty="isFormDirty"
                    />
                </a-tab-pane>
                <a-tab-pane key="audit-logs" :tab="tabTitle('audit-logs')">
                    <tab-audit-logs :user_id="user_id" ref="AuditLogsTab" />
                </a-tab-pane>
            </a-tabs>
        </div>
    </layout>
</template>

<script>
import Layout from '@/layouts/Layout.vue';
import TabGeneral from '@/components/tabs/user_settings/General.vue';
import TabSecurity from '@/components/tabs/user_settings/Security.vue';
import TabAuditLogs from '@/components/tabs/user_settings/AuditLogs.vue';

export default {
    components: {
        Layout,
        TabGeneral,
        TabSecurity,
        TabAuditLogs,
    },
    data() {
        return {
            user_id: this.$store.state.user.id,
            user: {},
            password_change: {
                user_id: this.$store.state.user.id,
                old_password: '',
                password: '',
                password_confirmation: '',
            },
            default_tab: 'general',
            validation: {
                errors: {},
                message: '',
            },
            processing: false,
            form_dirty: false,
        };
    },
    mounted() {
        const tab = this.$route.params.tab;
        this.setTab(tab);
        this.fetch();
    },
    beforeRouteLeave(to, from) {
        if (this.form_dirty) {
            return new Promise((resolve) => {
                this.$confirm({
                    title: this.$t('user.nav_modal.title'),
                    content: this.$t('user.nav_modal.content'),
                    onOk() {
                        resolve(true);
                    },
                    onCancel() {
                        resolve(false);
                    },
                });
            });
        }
    },
    methods: {
        setTab(tab) {
            switch (tab) {
                case 'security':
                    this.default_tab = 'security';
                    break;
                case 'audit-logs':
                    if (typeof this.$refs.AuditLogsTab !== 'undefined') {
                        this.$refs.AuditLogsTab.reload();
                    }
                    this.default_tab = 'audit-logs';
                    break;
                default:
                    this.default_tab = 'general';
                    break;
            }
        },
        tabTitle(key) {
            switch (key) {
                case 'main':
                    return this.$t('user.tabs.main');
                case 'general':
                    return this.$t('user.tabs.general');
                case 'security':
                    return this.$t('user.tabs.security');
                case 'audit-logs':
                    return this.$t('user.tabs.audit_logs');
            }
        },
        onTabChange(key) {
            const vm = this;

            if (this.form_dirty) {
                this.$confirm({
                    title: this.$t('user.nav_modal.title'),
                    content: this.$t('user.nav_modal.content'),
                    onOk() {
                        vm.form_dirty = false;
                        vm.setTab(key);
                        vm.fetch();

                        vm.$router.push({
                            name: 'user-settings-tab',
                            params: {
                                tab: key,
                            },
                        });
                    },
                    onCancel() {
                        vm.form_dirty = true;
                    },
                });
                this.form_dirty = vm.form_dirty;
            } else {
                this.setTab(key);
                this.fetch();

                this.$router.push({
                    name: 'user-settings-tab',
                    params: {
                        tab: key,
                    },
                });
            }
        },
        isFormDirty(is_dirty) {
            this.form_dirty = is_dirty;
        },
        fetch() {
            this.$http.get('/api/users/' + this.user_id).then((response) => {
                let userData = {
                    ...response.data.data,
                    roles: response.data.data.roles.join(', '),
                };

                this.user = userData;
            });
        },
        handleSubmit(data) {
            if (this.processing) {
                return false;
            }

            this.processing = true;

            const headers = { 'Content-Type': 'multipart/form-data' };

            this.$http
                .post('/api/users/self/' + this.user_id, data.data, { headers })
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$notification.success({ message: response.data.message });
                        this.form_dirty = false;
                    } else {
                        this.$notification.error({ message: response.data.message });
                    }
                })
                .catch((error) => {
                    this.$notification.error({ message: this.$t('user.tabs.failed', { tab: data.tab }) });

                    if (error.response.status == 422) {
                        this.validation = error.response.data;
                    } else if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVUS01' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        handlePasswordSubmit(data) {
            if (this.processing) {
                return false;
            }

            let payload = {
                ...data.data,
            };

            this.processing = true;

            this.$http
                .post('/api/users/self/change-password', payload)
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$notification.success({ message: response.data.message });

                        this.password_change = {
                            user_id: this.$store.state.user.id,
                            old_password: '',
                            password: '',
                            password_confirmation: '',
                        };
                        this.form_dirty = false;
                        this.$refs.SecurityTab.v$.$reset();
                    } else {
                        this.$notification.error({ message: response.data.message });
                    }
                })
                .catch((error) => {
                    this.$notification.error({ message: this.$t('user.tabs.failed', { tab: data.tab }) });

                    if (error.response.status == 422) {
                        this.validation = error.response.data;
                    } else if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVCP02' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
    },
};
</script>
